const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
    } else {
      const elementTarget = element.target;
      elementTarget.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      elementTarget.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
      // Reset the CSS animation so it can replay when the element re-enters
      elementTarget.style.animation = 'none';
      void elementTarget.offsetHeight; // force reflow
      elementTarget.style.animation = '';
      if (elementTarget.hasAttribute('data-cascade'))
        elementTarget.removeAttribute('style');
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    let scrollHandler = null;

    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;

        if (entry.isIntersecting && !scrollHandler) {
          // Attach scroll listener when element enters viewport
          scrollHandler = throttle(() => {
            if (!elementIsVisible) return;
            element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
          });
          window.addEventListener('scroll', scrollHandler, { passive: true });
        } else if (!entry.isIntersecting && scrollHandler) {
          // Remove scroll listener when element exits viewport to prevent leaks
          window.removeEventListener('scroll', scrollHandler);
          scrollHandler = null;
        }
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}

/* ============================================================
   LNC Enhanced Scroll Animations
   Stagger, Parallax, Counter, Split-Text
   All features respect prefers-reduced-motion.
   ============================================================ */

const LNC_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---- 1. Stagger Support ---- */
function initializeStaggerAnimations() {
  if (LNC_REDUCED_MOTION) return;

  const staggerContainers = document.querySelectorAll('[data-stagger]');
  if (!staggerContainers.length) return;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const container = entry.target;
        const items = container.querySelectorAll('[data-stagger-item]');
        items.forEach((item, i) => {
          item.style.setProperty('--animation-order', i);
          item.style.transitionDelay = `${i * 0.1}s`;
        });
        container.classList.add('is-visible');
        obs.unobserve(container);
      });
    },
    { rootMargin: '0px 0px -40px 0px' }
  );

  staggerContainers.forEach((el) => observer.observe(el));
}

/* ---- 2. Parallax Support ---- */
function initializeParallaxAnimations() {
  if (LNC_REDUCED_MOTION) return;

  const parallaxElements = document.querySelectorAll('[data-parallax]');
  if (!parallaxElements.length) return;

  const visibleSet = new Set();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          visibleSet.add(entry.target);
        } else {
          visibleSet.delete(entry.target);
        }
      });
    },
    { rootMargin: '100px 0px 100px 0px' }
  );

  parallaxElements.forEach((el) => observer.observe(el));

  let ticking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        visibleSet.forEach((el) => {
          const speed = parseFloat(el.dataset.parallax) || 0.3;
          const rect = el.getBoundingClientRect();
          const centerOffset = rect.top + rect.height / 2 - window.innerHeight / 2;
          const translateY = centerOffset * speed * -1;
          el.style.transform = `translateY(${translateY}px)`;
        });
        ticking = false;
      });
    },
    { passive: true }
  );
}

/* ---- 3. Counter Animation ---- */
function initializeCounterAnimations() {
  if (LNC_REDUCED_MOTION) {
    document.querySelectorAll('[data-count-to]').forEach((el) => {
      el.textContent = el.dataset.countTo;
    });
    return;
  }

  const counterElements = document.querySelectorAll('[data-count-to]');
  if (!counterElements.length) return;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        obs.unobserve(el);

        const target = parseInt(el.dataset.countTo, 10);
        if (isNaN(target)) return;
        const duration = parseInt(el.dataset.countDuration, 10) || 2000;
        let startTime = null;

        function step(timestamp) {
          if (!startTime) startTime = timestamp;
          const progress = Math.min((timestamp - startTime) / duration, 1);
          el.textContent = Math.floor(progress * target);
          if (progress < 1) {
            requestAnimationFrame(step);
          } else {
            el.textContent = target;
          }
        }

        requestAnimationFrame(step);
      });
    },
    { threshold: 0.3 }
  );

  counterElements.forEach((el) => observer.observe(el));
}

/* ---- 4. Split Text Animation ---- */
function initializeSplitTextAnimations() {
  if (LNC_REDUCED_MOTION) return;

  const splitElements = document.querySelectorAll('[data-split-text]');
  if (!splitElements.length) return;

  /* Wrap words in spans upfront (avoids layout shift on trigger) */
  splitElements.forEach((el) => {
    const text = el.textContent.trim();
    if (!text) return;
    el.setAttribute('aria-label', text);
    el.innerHTML = '';
    const words = text.split(/\s+/);
    words.forEach((word, i) => {
      const span = document.createElement('span');
      span.textContent = word;
      span.style.display = 'inline-block';
      span.style.opacity = '0';
      span.style.transform = 'translateY(12px)';
      span.style.transition = `opacity 0.4s ease, transform 0.4s ease`;
      span.style.transitionDelay = `${i * 0.06}s`;
      span.setAttribute('aria-hidden', 'true');
      el.appendChild(span);
      /* Add space between words */
      if (i < words.length - 1) {
        el.appendChild(document.createTextNode(' '));
      }
    });
  });

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        obs.unobserve(el);
        const spans = el.querySelectorAll('span');
        spans.forEach((span) => {
          span.style.opacity = '1';
          span.style.transform = 'translateY(0)';
        });
      });
    },
    { threshold: 0.2 }
  );

  splitElements.forEach((el) => observer.observe(el));
}

/* ---- Boot enhanced animations ---- */
function initializeLNCEnhancedAnimations() {
  initializeStaggerAnimations();
  initializeParallaxAnimations();
  initializeCounterAnimations();
  initializeSplitTextAnimations();
}

window.addEventListener('DOMContentLoaded', initializeLNCEnhancedAnimations);

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => initializeLNCEnhancedAnimations());
}
